import { IconPlus, IconTrash } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import stringify from 'fast-safe-stringify'
import { useState } from 'react'

import {
    AstraButton,
    AstraCallout,
    AstraCheckbox,
    AstraIcon,
    AstraInputWrapper,
    AstraParagraph,
    AstraSelect,
    AstraSkeletonLoader,
    AstraSwitch,
    AstraTextField,
    AstraTooltip,
    getOnlyDate,
} from '~/astra'
import { importStaffingSurgeryGroups } from '~/store/di-entity.api'
import { isNullish } from '~/utils/guards'

import { ExpertConfigSection } from '../../Form/ExpertConfigSection'
import { withFieldGroup } from '../../Form/formHooks'
import { FieldGroupWrapper, FormSectionWrapperProps } from '../../Form/FormWrapper'
import { createFormFieldMetaProps, formatNumberList, parseIntOrZero, parseNumberList } from '../../Form/utils'
import {
    DEFAULT_EXTRA_STAFFING_CONFIG,
    defaultFormValues,
    extraStaffingConfigSchema,
    type FormExtraStaffingConfig,
    type FormPractitionerProfile,
} from './formState'

const defaultValues = defaultFormValues.activeProfile

const toRawJson = (config: FormExtraStaffingConfig | undefined | null) => stringify(config, undefined, 2)

const WorkDonePerSessionField = ({
    value,
    onChange,
    error,
    errorText,
}: {
    value: number[] | undefined | null
    onChange: (value: number[]) => void
    error?: boolean
    errorText?: string
}) => {
    const [draft, setDraft] = useState(() => formatNumberList(value))

    return (
        <AstraTextField
            label="Arbeid per økt"
            helperText="work_done_per_session - kommaseparert"
            value={draft}
            onChange={e => {
                const nextDraft = e.target.value
                setDraft(nextDraft)
                onChange(parseNumberList(nextDraft))
            }}
            onBlur={() => setDraft(formatNumberList(parseNumberList(draft)))}
            error={error}
            errorText={errorText}
            fullWidth
        />
    )
}

const WaitingListField = ({
    profile,
    value,
    onChange,
}: {
    profile: FormPractitionerProfile
    value: Record<string, number> | undefined | null
    onChange: (value: Record<string, number> | undefined) => void
}) => {
    const profileDateString = profile.activeFrom ? getOnlyDate(profile.activeFrom) : undefined

    const { data: staffingSurgeryGroupsData, isLoading } = useQuery({
        queryKey: ['staffingSurgeryGroups', profileDateString],
        queryFn: () =>
            // loads all ssgs if activeFrom is not set (undefined are omitted from the query)
            importStaffingSurgeryGroups({
                'planning_period.start_date:lte': profileDateString,
                'planning_period.end_date:gte': profileDateString,
            }),
        // ensure unique groups
        select: data => Array.from(new Map(data.data?.map(group => [group.name, group])).values()).toSorted((a, b) => a.name.localeCompare(b.name)),
        staleTime: 60 * 1000,
    })
    const staffingSurgeryGroups = staffingSurgeryGroupsData ?? []
    const waitingListEntries = Object.entries(value ?? {})
    const usedGroupNames = new Set(waitingListEntries.map(([name]) => name))

    const updateEntryName = (previousName: string, nextName?: string) => {
        if (previousName === nextName) return
        const next = Object.fromEntries(waitingListEntries.map(([name, count]) => [name === previousName ? nextName : name, count]))
        onChange(next)
    }

    const updateEntryValue = (name: string, nextValue: number) => {
        onChange({ ...(value ?? {}), [name]: nextValue })
    }

    const removeEntry = (name: string) => {
        const next = { ...(value ?? {}) }
        delete next[name]
        onChange(Object.keys(next).length > 0 ? next : undefined)
    }

    const addEntry = () => {
        const nextName = staffingSurgeryGroups.find(group => !usedGroupNames.has(group.name))?.name
        if (!nextName) return
        onChange({ ...(value ?? {}), [nextName]: 0 })
    }

    const hasAddedAllAvailableGroups = waitingListEntries.length !== 0 && waitingListEntries.length === staffingSurgeryGroups.length
    const canAddEntry = !hasAddedAllAvailableGroups && staffingSurgeryGroups.length > 0
    return (
        <div className="flex flex-col gap-3">
            <div className="flex items-center justify-between gap-4">
                <div>
                    <AstraParagraph weight="medium">Venteliste</AstraParagraph>
                    <AstraParagraph className="text-muted">waiting_list</AstraParagraph>
                </div>
                <AstraTooltip tooltipContent={!canAddEntry ? 'Ingen bemanningsgrupper kan bli lagt til' : 'Legg til bemanningsgruppe'}>
                    <AstraButton
                        type="button"
                        variant="secondary"
                        size="small"
                        label="Legg til"
                        icon={<AstraIcon icon={IconPlus} />}
                        onClick={addEntry}
                        disabled={!canAddEntry}
                    />
                </AstraTooltip>
            </div>
            {staffingSurgeryGroups.length === 0 && <AstraCallout color="caution" text="Fant ingen bemanningsgrupper for denne perioden." />}
            {waitingListEntries.length === 0 ? (
                <AstraParagraph className="text-muted">Ingen ventelisteverdier er satt.</AstraParagraph>
            ) : (
                waitingListEntries.map(([name, count]) => {
                    const options = staffingSurgeryGroups
                        .filter(group => !usedGroupNames.has(group.name))
                        .map(group => ({ label: group.name, value: group.name }))

                    if (!options.some(option => option.value === name)) {
                        options.unshift({ label: name, value: name })
                    }

                    return (
                        <div key={name} className="grid grid-cols-3 gap-3">
                            {isLoading ? (
                                <AstraSkeletonLoader />
                            ) : (
                                <AstraSelect label="Bemanningsgruppe" options={options} value={name} onChange={nextName => updateEntryName(name, nextName)} />
                            )}
                            <AstraTextField
                                type="number"
                                min={0}
                                label="Verdi"
                                value={String(count)}
                                onChange={event => updateEntryValue(name, parseIntOrZero(event.target.value))}
                            />
                            <div className="pt-5">
                                <AstraButton
                                    type="button"
                                    variant="secondary"
                                    size="medium"
                                    icon={<AstraIcon icon={IconTrash} />}
                                    onClick={() => removeEntry(name)}
                                />
                            </div>
                        </div>
                    )
                })
            )}
        </div>
    )
}

type PractitionerStaffingConfigFieldsProps = {
    size?: FormSectionWrapperProps['size']
}
const defaultProps: PractitionerStaffingConfigFieldsProps = {
    size: 'default',
}
export const PractitionerStaffingConfigFields = withFieldGroup({
    defaultValues,
    props: defaultProps,
    render: function Render({ group, size }) {
        const [mode, setMode] = useState<'visual' | 'raw'>('visual')
        const [rawJsonError, setRawJsonError] = useState<string | undefined>()

        const profile = group.state.values
        const isRawMode = mode === 'raw'

        const handleModeChange = (nextMode: typeof mode) => {
            setMode(nextMode)
            setRawJsonError(undefined)
        }

        const handleRawJsonChange = (value: string) => {
            try {
                const parsed = extraStaffingConfigSchema.parse(JSON.parse(value))
                group.setFieldValue('extraStaffingConfig', parsed)
                setRawJsonError(undefined)
            } catch (e) {
                const errorMessage = e instanceof Error ? e.message : 'Ukjent feil'
                setRawJsonError(`Ugyldig JSON eller feil format: ${errorMessage}`)
            }
        }
        return (
            <group.AppField name="extraStaffingConfig">
                {rootField => (
                    <ExpertConfigSection
                        title="Bemanningskonfigurasjon"
                        description="Overstyrer verdier i extra_staffing_config for denne kirurgprofilen."
                        mode={mode}
                        onModeChange={handleModeChange}
                        size={size}
                    >
                        <label className="flex w-fit cursor-pointer items-center gap-2">
                            <AstraSwitch
                                checked={!isNullish(rootField.state.value)}
                                onCheckedChange={checked => rootField.handleChange(checked ? DEFAULT_EXTRA_STAFFING_CONFIG : null)}
                            />
                            <AstraParagraph>Inkluder kirurgprofilen i generering av bemanningsplan</AstraParagraph>
                        </label>

                        {isNullish(rootField.state.value) ? null : isRawMode ? (
                            <AstraInputWrapper error={!!rawJsonError} errorText={rawJsonError}>
                                <textarea
                                    className="h-96 w-full bg-transparent outline-none"
                                    defaultValue={toRawJson(rootField.state.value)}
                                    onChange={e => handleRawJsonChange(e.target.value)}
                                />
                            </AstraInputWrapper>
                        ) : (
                            <>
                                <FieldGroupWrapper>
                                    <group.AppField name="extraStaffingConfig.num_ox">
                                        {field => (
                                            <AstraTextField
                                                type="number"
                                                min={0}
                                                label="Antall vakter i perioden"
                                                helperText="num_ox"
                                                value={String(field.state.value)}
                                                onChange={e => field.handleChange(parseIntOrZero(e.target.value))}
                                                fullWidth
                                                {...createFormFieldMetaProps(field)}
                                            />
                                        )}
                                    </group.AppField>
                                    <group.AppField name="extraStaffingConfig.work_gained_per_poli_day">
                                        {field => (
                                            <AstraTextField
                                                type="number"
                                                label="Arbeid per polidag"
                                                helperText="work_gained_per_poli_day"
                                                value={String(field.state.value)}
                                                onChange={e => field.handleChange(parseIntOrZero(e.target.value))}
                                                {...createFormFieldMetaProps(field)}
                                                fullWidth
                                            />
                                        )}
                                    </group.AppField>
                                    <group.AppField name="extraStaffingConfig.work_gained_per_evening_poli_day">
                                        {field => (
                                            <AstraTextField
                                                type="number"
                                                label="Arbeid per kveldspolidag"
                                                helperText="work_gained_per_evening_poli_day"
                                                value={String(field.state.value)}
                                                onChange={e => field.handleChange(parseIntOrZero(e.target.value))}
                                                fullWidth
                                                {...createFormFieldMetaProps(field)}
                                            />
                                        )}
                                    </group.AppField>
                                    <group.AppField name="extraStaffingConfig.work_done_per_session">
                                        {field => (
                                            <WorkDonePerSessionField
                                                value={field.state.value}
                                                onChange={field.handleChange}
                                                {...createFormFieldMetaProps(field)}
                                            />
                                        )}
                                    </group.AppField>
                                </FieldGroupWrapper>

                                <group.AppField name="extraStaffingConfig.exclude_from_too_few_surgeons_warning">
                                    {field => (
                                        <label className="flex w-fit cursor-pointer items-center gap-2">
                                            <AstraCheckbox
                                                checked={field.state.value ?? false}
                                                onCheckedChange={checked => field.handleChange(checked === true)}
                                            />
                                            <AstraParagraph>Ekskluder fra «for få kirurger»-varsel</AstraParagraph>
                                        </label>
                                    )}
                                </group.AppField>

                                <group.AppField name="extraStaffingConfig.waiting_list">
                                    {field => <WaitingListField profile={profile} value={field.state.value} onChange={field.handleChange} />}
                                </group.AppField>
                            </>
                        )}
                    </ExpertConfigSection>
                )}
            </group.AppField>
        )
    },
})
